/**
 * 公式库：人类知识史上真实的高级数学 / 物理公式（LaTeX）。
 *
 * 按学科分组，可无限追加——登录页背景从这里面取样，
 * 铺成「知识的演算纸」。每一条都必须是真实存在的公式，
 * 内行一眼能认出：这是字典，不是装饰性乱码。
 */

export interface FormulaGroup {
  readonly field: string;
  readonly items: readonly string[];
}

export const FORMULA_GROUPS: readonly FormulaGroup[] = [
  {
    field: '数学分析',
    items: [
      'e^{i\\pi} + 1 = 0',
      '\\int_{-\\infty}^{\\infty} e^{-x^2}\\,dx = \\sqrt{\\pi}',
      '\\sum_{n=1}^{\\infty} \\frac{1}{n^2} = \\frac{\\pi^2}{6}',
      "\\frac{d}{dx}\\left[f(g(x))\\right] = f'(g(x))\\,g'(x)",
      '\\int_a^b f(x)\\,dx = F(b) - F(a)',
      '\\lim_{x \\to 0} \\frac{\\sin x}{x} = 1',
      '\\Gamma(n+1) = n!',
      '\\zeta(s) = \\sum_{n=1}^{\\infty} \\frac{1}{n^s}',
      '\\int_{-\\infty}^{\\infty} \\frac{e^{i\\omega x}}{1+e^{i\\omega}} \\, d\\omega',
      'f(x) = \\sum_{n=0}^{\\infty} \\frac{f^{(n)}(a)}{n!}(x-a)^n',
      '\\int_{\\gamma} f(z)\\,dz = 2\\pi i \\sum \\operatorname{Res}(f, z_k)',
      '\\nabla \\cdot \\nabla f = \\Delta f',
      '\\int_0^\\infty \\frac{x^{s-1}}{e^x - 1}\\,dx = \\Gamma(s)\\zeta(s)',
      '\\left(\\sum_{k=1}^{n} a_k b_k\\right)^2 \\le \\left(\\sum a_k^2\\right)\\left(\\sum b_k^2\\right)',
      '\\frac{\\partial^2 u}{\\partial t^2} = c^2 \\nabla^2 u',
      '\\operatorname{li}(x) = \\int_0^x \\frac{dt}{\\ln t}',
      'B(p,q) = \\int_0^1 t^{p-1}(1-t)^{q-1}\\,dt',
      '\\int_{0}^{\\infty} \\cos(x^2)\\,dx = \\sqrt{\\frac{\\pi}{8}}',
      '\\prod_{p} \\frac{1}{1-p^{-s}} = \\zeta(s)',
      '\\int_{\\Omega} |\\nabla u|^2 \\, dx',
    ],
  },
  {
    field: '微分方程与动力系统',
    items: [
      '\\ddot{x} + 2\\zeta\\omega_n\\dot{x} + \\omega_n^2 x = 0',
      'i\\hbar\\frac{\\partial}{\\partial t}\\Psi = \\hat{H}\\Psi',
      '\\frac{\\partial u}{\\partial t} = D\\frac{\\partial^2 u}{\\partial x^2}',
      '\\frac{dx}{dt} = \\sigma(y - x),\\; \\frac{dy}{dt} = x(\\rho - z) - y,\\; \\frac{dz}{dt} = xy - \\beta z',
      'm\\frac{d^2\\vec{r}}{dt^2} = \\vec{F}',
      '\\frac{d\\vec{L}}{dt} = \\vec{\\tau}',
      '\\dot{x} = f(x) + \\eta(t)',
      'H(p,q) = \\sum_i \\dot{q}_i p_i - L(q, \\dot{q}, t)',
      '\\dot{q}_i = \\frac{\\partial H}{\\partial p_i},\\quad \\dot{p}_i = -\\frac{\\partial H}{\\partial q_i}',
      '\\{f, g\\} = \\sum_i \\left( \\frac{\\partial f}{\\partial q_i}\\frac{\\partial g}{\\partial p_i} - \\frac{\\partial f}{\\partial p_i}\\frac{\\partial g}{\\partial q_i} \\right)',
      '\\frac{d^2 u}{d\\phi^2} + u = \\frac{GM}{h^2}',
      '\\tau \\frac{dV}{dt} = -V + f(V, I)',
      '\\frac{\\partial \\rho}{\\partial t} + \\nabla \\cdot \\mathbf{j} = 0',
      'dX_t = \\mu X_t\\,dt + \\sigma X_t\\,dW_t',
      '\\frac{dN}{dt} = rN\\left(1 - \\frac{N}{K}\\right)',
    ],
  },
  {
    field: '线性代数与抽象代数',
    items: [
      'A\\vec{v} = \\lambda\\vec{v}',
      'e^{A} = \\sum_{k=0}^{\\infty} \\frac{A^k}{k!}',
      '\\det(A - \\lambda I) = 0',
      'AB \\ne BA',
      'G/H \\text{ is a group} \\iff H \\trianglelefteq G',
      '(G, \\cdot) \\cong (\\mathbb{R}, +)',
      '\\operatorname{Gal}(K/F) \\text{ acts on } K',
      'V^{*} \\otimes W \\cong \\operatorname{Hom}(V, W)',
      '\\operatorname{tr}(AB) = \\operatorname{tr}(BA)',
      'A^T A^{-1}',
      '\\ker T / \\operatorname{im} T',
      '\\mathbb{Z}/n\\mathbb{Z}',
      'x^p \\equiv x \\pmod{p}',
      '\\sigma(g \\cdot x) = \\sigma(g) \\cdot x',
      '\\bigwedge^k V',
      '\\operatorname{rank}(A) + \\operatorname{nullity}(A) = n',
      '\\|\\vec{u} \\times \\vec{v}\\| = \\|\\vec{u}\\|\\|\\vec{v}\\|\\sin\\theta',
      'SU(2) \\to SO(3)',
    ],
  },
  {
    field: '概率统计与信息论',
    items: [
      'P(A|B) = \\frac{P(B|A)\\,P(A)}{P(B)}',
      'H(X) = -\\sum_i p_i \\log_2 p_i',
      'D_{KL}(P \\parallel Q) = \\sum_i p_i \\ln\\frac{p_i}{q_i}',
      'E[X] = \\int_{-\\infty}^{\\infty} x\\,f(x)\\,dx',
      '\\operatorname{Var}(X) = E[X^2] - (E[X])^2',
      'f(x; \\mu, \\sigma) = \\frac{1}{\\sigma\\sqrt{2\\pi}} e^{-\\frac{(x-\\mu)^2}{2\\sigma^2}}',
      '\\lim_{n \\to \\infty} P\\left(\\left|\\bar{X}_n - \\mu\\right| \\ge \\epsilon\\right) = 0',
      '\\chi^2 = \\sum \\frac{(O_i - E_i)^2}{E_i}',
      'I(X;Y) = H(X) + H(Y) - H(X,Y)',
      'p(\\theta \\mid D) \\propto p(D \\mid \\theta)\\,p(\\theta)',
      '\\frac{\\hat{\\beta} - \\beta}{\\operatorname{se}(\\hat{\\beta})} \\sim t_{n-k}',
      'W = \\sum_i \\frac{(N_i - np_i)^2}{np_i}',
      'C = 1 - \\frac{H}{\\log n}',
      'F(s) = \\int_0^{\\infty} f(t) e^{-st}\\,dt',
    ],
  },
  {
    field: '量子力学',
    items: [
      '\\hat{p} = -i\\hbar\\nabla',
      '[\\hat{x}, \\hat{p}] = i\\hbar',
      '\\Delta x \\, \\Delta p \\ge \\frac{\\hbar}{2}',
      '\\hat{H}\\ket{\\psi} = E\\ket{\\psi}',
      '\\ket{\\psi} = \\alpha\\ket{0} + \\beta\\ket{1}',
      'P = |\\langle \\phi | \\psi \\rangle|^2',
      '\\sigma_i \\sigma_j = \\delta_{ij} I + i \\epsilon_{ijk} \\sigma_k',
      'E = mc^2',
      '\\psi(x,t) = e^{i(kx - \\omega t)}',
      '\\langle \\psi | \\hat{A} | \\psi \\rangle',
      '\\hat{L}_z = -i\\hbar\\frac{\\partial}{\\partial \\phi}',
      'H = \\frac{\\hat{p}^2}{2m} + V(x)',
      '|\\psi(x)|^2\\,dx',
      '\\sum_n |n\\rangle\\langle n| = \\mathbb{I}',
      'U(1) \\times SU(2) \\times SU(3)',
      'e^{-\\beta \\hat{H}}',
    ],
  },
  {
    field: '相对论与场论',
    items: [
      'G_{\\mu\\nu} + \\Lambda g_{\\mu\\nu} = \\frac{8\\pi G}{c^4} T_{\\mu\\nu}',
      'R_{\\mu\\nu} - \\frac{1}{2} R g_{\\mu\\nu} = 0',
      'ds^2 = -c^2 dt^2 + dx^2 + dy^2 + dz^2',
      '\\nabla_\\mu T^{\\mu\\nu} = 0',
      '\\mathcal{L} = -\\frac{1}{4} F_{\\mu\\nu}F^{\\mu\\nu}',
      'F_{\\mu\\nu} = \\partial_\\mu A_\\nu - \\partial_\\nu A_\\mu',
      '\\partial_\\mu F^{\\mu\\nu} = \\mu_0 J^\\nu',
      'S = -m_0 c \\int ds',
      '\\mathcal{L}_{YM} = -\\frac{1}{4} \\operatorname{tr}(F_{\\mu\\nu} F^{\\mu\\nu})',
      '\\delta S = 0',
      '\\Gamma^{\\lambda}_{\\mu\\nu} = \\frac{1}{2} g^{\\lambda\\sigma}(\\partial_\\mu g_{\\sigma\\nu} + \\partial_\\nu g_{\\sigma\\mu} - \\partial_\\sigma g_{\\mu\\nu})',
      'E^2 = (pc)^2 + (m_0c^2)^2',
      '\\Box \\phi = 0',
      'T_{\\mu\\nu} = (\\rho + p)u_\\mu u_\\nu + p g_{\\mu\\nu}',
      'S_{CS} = \\frac{k}{4\\pi}\\int \\operatorname{tr}\\left(A \\wedge dA + \\frac{2}{3} A \\wedge A \\wedge A\\right)',
    ],
  },
  {
    field: '统计力学与热力学',
    items: [
      'S = k_B \\ln \\Omega',
      'Z = \\sum_i e^{-\\beta E_i}',
      '\\langle E \\rangle = -\\frac{\\partial \\ln Z}{\\partial \\beta}',
      'dS \\ge \\frac{\\delta Q}{T}',
      'PV = Nk_BT',
      '\\Delta F = \\Delta U - T\\Delta S',
      'P(i) = \\frac{e^{-E_i/k_BT}}{Z}',
      'S = -k_B \\operatorname{tr}(\\rho \\ln \\rho)',
      '\\kappa = \\frac{\\langle \\delta E^2 \\rangle}{k_B T^2}',
      'dU = T\\,dS - P\\,dV + \\mu\\,dN',
      'C = T\\left(\\frac{\\partial S}{\\partial T}\\right)_V',
      'e^{\\beta} = \\lambda_T^3 / v',
    ],
  },
  {
    field: '几何与拓扑',
    items: [
      '\\int_{M} K \\, dA = 2\\pi \\chi(M)',
      '\\partial \\partial M = \\emptyset',
      'd\\omega = \\partial_i \\omega_j\\, dx^i \\wedge dx^j',
      '\\int_{\\partial \\Omega} \\omega = \\int_{\\Omega} d\\omega',
      'R_{\\mu\\nu\\lambda\\kappa} = g_{\\mu\\sigma} R^{\\sigma}_{\\;\\nu\\lambda\\kappa}',
      '\\pi_1(S^1) = \\mathbb{Z}',
      'H^n(M) = \\operatorname{ker} d_n / \\operatorname{im} d_{n-1}',
      'c_1(E) = \\frac{i}{2\\pi}\\operatorname{tr} F',
      '\\chi(S^2) = 2',
      '\\hat{A}(M)',
      '\\operatorname{ind}(D) = \\hat{A}(M)\\,\\operatorname{ch}(E)',
      'g = 1 + \\frac{1}{2\\pi}\\int K\\,dA',
      'd(F \\wedge G) = dF \\wedge G + (-1)^p F \\wedge dG',
      'K > 0 \\Rightarrow \\pi_1(M) \\text{ finite}',
    ],
  },
  {
    field: '数论与代数几何',
    items: [
      'x^n + y^n = z^n,\\; n > 2 \\Rightarrow \\text{no solutions}',
      '\\operatorname{Re}(s) = \\frac{1}{2}',
      'y^2 = x^3 + ax + b',
      '\\left| \\#E(\\mathbb{F}_p) - (p+1) \\right| \\le 2\\sqrt{p}',
      'f(\\gamma z) = (cz+d)^k f(z)',
      '\\left( \\frac{p}{q} \\right) \\left( \\frac{q}{p} \\right) = (-1)^{\\frac{p-1}{2}\\frac{q-1}{2}}',
      'x^p \\equiv x \\pmod{p}',
      '\\left| x \\right|_p \\cdot \\left| y \\right|_p = \\left| xy \\right|_p',
      'L(E, s) = \\sum_n \\frac{a_n}{n^s}',
      '\\operatorname{ord}_p(n!) = \\sum_i \\left\\lfloor \\frac{n}{p^i} \\right\\rfloor',
      '\\pi(x) \\sim \\frac{x}{\\ln x}',
      '\\Delta = -\\frac{1}{1728}\\left( E_4^3 - E_6^2 \\right)',
      'K(x, y) = \\sum_n \\phi_n(x) \\overline{\\phi_n(y)}',
      '\\#C(\\mathbb{F}_{q^m}) = q^m + 1 - \\sum \\alpha_i^m',
    ],
  },
  {
    field: '表示论与李群',
    items: [
      '[X, Y] = XY - YX',
      '\\operatorname{Ad}(\\exp X) = \\exp(\\operatorname{ad} X)',
      '\\chi_\\pi(g) = \\operatorname{tr} \\pi(g)',
      'g = \\exp(X_1) \\cdots \\exp(X_n)',
      '\\dim \\mathfrak{g} = 248',
      'R_{\\mu\\nu}^{a} = 0',
      '\\bigoplus_{\\lambda} V_\\lambda \\otimes M_\\lambda',
      '\\operatorname{Res}^G_H \\operatorname{Ind}^G_H V \\cong V \\otimes \\mathbb{C}[G/H]',
      'W = N_G(T) / T',
      'e^{2\\pi i \\langle \\lambda, \\rho^\\vee \\rangle} = 1',
    ],
  },
  {
    field: '范畴论',
    items: [
      'F(g \\circ f) = F(g) \\circ F(f)',
      '\\operatorname{Hom}(\\_, A) \\dashv \\operatorname{Hom}(A, \\_)',
      '\\operatorname{Nat}(\\operatorname{Hom}(-, A), F) \\cong F(A)',
      '\\operatorname{Hom}(FX, Y) \\cong \\operatorname{Hom}(X, GY)',
      '\\varprojlim F \\; / \\; \\varinjlim F',
      '\\mu \\circ \\mu = \\mu \\circ (T\\mu)',
      'X \\to Y \\to Z',
      '\\operatorname{Ob}(C) \\times \\operatorname{Mor}(C)',
      '\\eta_X : X \\to GFX',
    ],
  },
  {
    field: '量子场论与弦论',
    items: [
      'Z = \\int \\mathcal{D}\\phi \\; e^{iS[\\phi]/\\hbar}',
      '\\langle 0 | T\\{ \\phi(x) \\phi(y) \\} | 0 \\rangle',
      '\\mathcal{L} = \\bar{\\psi}(i\\gamma^\\mu D_\\mu - m)\\psi - \\frac{1}{4}F_{\\mu\\nu}F^{\\mu\\nu}',
      'Z_{\\mathrm{grav}} = Z_{\\mathrm{CFT}}',
      'c_1(X) = 0',
      'W(C) = \\operatorname{tr}_R \\, \\mathcal{P} \\exp \\left( i \\oint_C A \\right)',
      'D_\\mu \\psi = (\\partial_\\mu + igA_\\mu)\\psi',
      '\\delta S = \\int d^4x \\; \\bar{\\psi} \\gamma^\\mu \\psi \\, \\partial_\\mu \\alpha',
      'S = 2\\pi \\sqrt{N}',
      'G_\\mu = 0',
      '\\langle \\phi_1 \\cdots \\phi_n \\rangle = \\frac{\\int \\mathcal{D}\\phi \\; \\phi_1 \\cdots \\phi_n \\, e^{iS}}{\\int \\mathcal{D}\\phi \\; e^{iS}}',
      'D = 10,\\; \\chi = 24',
      'T = \\\\frac{1}{2\\\\pi \\\\alpha^\\\\prime}',
      '\\hat{c} = \\frac{c}{3}',
    ],
  },
  {
    field: '凝聚态物理',
    items: [
      'H_{BCS} = \\sum_k \\epsilon_k c_k^\\dagger c_k - \\Delta \\sum_k c_{k\\uparrow}^\\dagger c_{-k\\downarrow}^\\dagger',
      '\\sigma_{xy} = \\nu \\frac{e^2}{h}',
      'H = -t \\sum_{\\langle ij \\rangle} c_i^\\dagger c_j + U \\sum_i n_{i\\uparrow} n_{i\\downarrow}',
      '\\gamma_{ij} \\in \\mathbb{Z}_2',
      '\\Delta_k = \\Delta_0 \\cos k_x',
      'v = \\langle \\psi | \\hat{v} | \\psi \\rangle',
      'm^* = \\frac{\\hbar^2}{\\partial^2 E / \\partial k^2}',
      '\\omega_c = \\frac{eB}{m}',
      'T_c = 1.14 \\, \\Theta_D \\, e^{-1/(N(0)V)}',
      'G(E) = (E - H + i0^+)^{-1}',
    ],
  },
  {
    field: '流体与湍流',
    items: [
      '\\mathrm{Re} = \\frac{\\rho v L}{\\mu}',
      'E(k) \\propto k^{-5/3}',
      'v \\cdot \\nabla v = -\\frac{1}{\\rho}\\nabla p + \\nu \\nabla^2 v',
      '\\omega = \\nabla \\times v',
      '\\mathrm{Ra} = \\frac{g \\beta \\Delta T L^3}{\\nu \\kappa}',
      '\\frac{D\\rho}{Dt} + \\rho \\nabla \\cdot v = 0',
      'Ma = \\frac{v}{c_s}',
      '\\nu_t = l^2 \\left| \\frac{\\partial v}{\\partial y} \\right|',
      '\\eta = (\\nu^3 / \\epsilon)^{1/4}',
    ],
  },
  {
    field: '控制论与密码学',
    items: [
      'c \\equiv m^e \\pmod{n}',
      '\\dot{x} = Ax + Bu, \\; y = Cx + Du',
      'P_{k+1} = A P_k A^T + Q',
      'u = -Kx',
      'A^T P + PA - PBR^{-1}B^T P + Q = 0',
      'd \\in \\mathbb{Z}^n, \\; \\|d\\| \\text{ short}',
      '\\gcd(a, n) = 1',
      'H(k_1 \\| m) \\ne H(k_2 \\| m)',
      's = k^{-1}(H(m) + xr) \\bmod q',
      '\\|Ax - b\\|_2 + \\lambda \\|x\\|_1',
      '\\min_u \\int_0^T \\left( x^T Q x + u^T R u \\right) dt',
    ],
  },
  {
    field: '机器学习与统计学习',
    items: [
      '\\operatorname{softmax}(z)_i = \\frac{e^{z_i}}{\\sum_j e^{z_j}}',
      'H(p, q) = -\\sum_x p(x) \\log q(x)',
      '\\operatorname{Attention}(Q,K,V) = \\operatorname{softmax}\\left( \\frac{QK^T}{\\sqrt{d_k}} \\right) V',
      '\\mathcal{L}_{ELBO} = E_q[\\log p(x|z)] - D_{KL}(q(z|x) \\parallel p(z))',
      '\\min_G \\max_D \\; E[\\log D(x)] + E[\\log(1 - D(G(z)))]',
      'x_{t-1} = \\frac{1}{\\sqrt{\\alpha_t}} \\left( x_t - \\frac{1-\\alpha_t}{\\sqrt{1-\\bar{\\alpha}_t}} \\epsilon_\\theta \\right)',
      '\\nabla_\\theta J(\\theta) = E_{\\pi_\\theta} \\left[ \\nabla_\\theta \\log \\pi_\\theta(a|s) Q^\\pi(s,a) \\right]',
      'w_{t+1} = w_t - \\eta \\nabla \\mathcal{L}(w_t)',
      'R(D) = \\min_{p(\\hat{x}|x): E[d] \\le D} I(X; \\hat{X})',
      'VC\\text{-dim} = d',
      '\\epsilon_{\\text{gen}} \\le \\sqrt{\\frac{\\log |\\mathcal{H}| + \\log(1/\\delta)}{2n}}',
      'K(x_i, x_j) = \\langle \\phi(x_i), \\phi(x_j) \\rangle',
      '\\Delta L \\approx \\frac{1}{2} \\epsilon^2 \\nabla^2 L',
      '\\rho(\\theta_1, \\theta_2) \\to 0',
    ],
  },
  {
    field: '天体物理与宇宙学',
    items: [
      'v = H_0 d',
      'ds^2 = -c^2dt^2 + a^2(t)\\left[ dr^2 + S_k^2(r) d\\Omega^2 \\right]',
      'H^2 = \\left( \\frac{\\dot{a}}{a} \\right)^2 = \\frac{8\\pi G}{3}\\rho - \\frac{k}{a^2} + \\frac{\\Lambda}{3}',
      'r_s = \\frac{2GM}{c^2}',
      'T_H = \\frac{\\hbar c^3}{8\\pi G M k_B}',
      'M > \\frac{\\hbar c}{G m_p^2}^{3/2}',
      '1 + z = \\frac{a_0}{a}',
      '\\Omega_k + \\Omega_\\Lambda + \\Omega_m = 1',
      'L = 4\\pi R^2 \\sigma T^4',
      '\\frac{dN}{dt} = 4\\pi r^2 \\rho v',
      'M = \\frac{v^2 r}{G}',
    ],
  },
  {
    field: '生物数学',
    items: [
      'p^2 + 2pq + q^2 = 1',
      '\\frac{\\partial u}{\\partial t} = D_u \\nabla^2 u + f(u,v)',
      'I = g_{Na} m^3 h (V - E_{Na}) + g_K n^4 (V - E_K)',
      'Y = a M^{3/4}',
      '\\frac{dx}{dt} = \\alpha xy - \\beta x',
      'R_0 = \\beta \\langle k \\rangle / \\gamma',
      'N(t) = N_0 e^{rt}',
      '\\dot{p} = p(1-p) \\left[ w_{AA}p + w_{Aa}q - \\bar{w} \\right]',
    ],
  },
  {
    field: '优化与计算',
    items: [
      '\\min_x \\; f(x) \\; \\text{s.t.} \\; g(x) \\le 0',
      '\\nabla^2 f(x) \\succeq 0',
      'x_{k+1} = x_k - \\alpha \\nabla f(x_k)',
      'L(x, \\lambda) = f(x) + \\lambda^T g(x)',
      '\\mathcal{L} = \\sum_i \\left( y_i - w^T x_i \\right)^2 + \\lambda \\|w\\|^2',
      'O(n \\log n)',
      'P \\stackrel{?}{=} NP',
      '\\max \\; c^T x \\; \\text{s.t.} \\; Ax \\le b',
      'J^* = \\min_\\pi E\\left[\\sum_t \\gamma^t r_t\\right]',
      '\\nabla_\\theta J = E\\left[\\nabla_\\theta \\log \\pi_\\theta(a|s)\\, Q\\right]',
      '\\epsilon_i \\overset{iid}{\\sim} \\mathcal{N}(0, \\sigma^2)',
      'A^* = \\arg\\min_A \\|Y - XA\\|_F^2 + \\lambda\\|A\\|_1',
      '\\text{KL}(q \\parallel p) - \\mathcal{L}(q)',
      'y = \\sigma(Wx + b)',
    ],
  },
];

/** 摊平的全量公式列表（可无限追加：往上面的分组里加即可） */
export const ALL_FORMULAS: readonly string[] = FORMULA_GROUPS.flatMap((g) => g.items);

/** 带学科信息的公式（前景显影层用：学科标签随公式可见） */
export interface TaggedFormula {
  readonly tex: string;
  readonly field: string;
}
export const TAGGED_FORMULAS: readonly TaggedFormula[] = FORMULA_GROUPS.flatMap((g) =>
  g.items.map((tex) => ({ tex, field: g.field })),
);
